import React, { use, useState } from "react";
import styled from "styled-components";
import { settings } from "../data/settings";
import theme from "../theme";
import { useLocation } from "react-router-dom";
import { RDVButton } from "./commonWidgets";
import SEO from "./SEO";
import AnimateOnScroll from "./AnimateOnScroll";
import HorizontalCarousel from "./HorizontalCarousel";

// --- Styled Components ---
const Banner = styled.div`
  background-image: ${({ image }) => `url(${image})`};
  background-size: cover;
  background-position: center;
  color: white;
  padding: calc(${settings.navbar.height} + 2rem) 0rem 1rem 0rem;
  text-align: center;
  position: relative;
  margin: auto;
  background-color: var(--my-color3);

  ${theme.breakpoints.down("sm")} {
    padding-right: 1rem;
    padding-left: 1rem;
  }
`;

const BannerTitle = styled.div`
  font-size: 1.5rem;
  color: var(--my-color2);
  max-width: 800px;
  margin: auto;
  text-transform: uppercase;
  margin-bottom: 1rem;
  font-weight: bold;

  ${theme.breakpoints.down("sm")} {
    font-size: 1.2rem;
    text-align: center;
  }
`;
const BannerSubTitle = styled.div`
  max-width: 800px;
  margin: auto;
  font-size: 1.1rem;
  color: white;
  ${theme.breakpoints.down("sm")} {
    font-size: 1rem;
    text-align: center;
  }
`;

const SectionContainer = styled.section`
  padding: 2rem 1rem 0rem;
  max-width: 800px;
  margin: auto;
`;

const Content = styled.div`
  max-width: 1000px;
  margin: auto;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? "row-reverse" : "row")};
  width: 100%;
  gap: 1rem;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column-reverse;
    align-items: center;
    gap: 0rem;
  }
`;

const HeaserTextSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: ${({ reverse }) => (reverse ? "flex-start" : "flex-end")};

  ${theme.breakpoints.down("sm")} {
    align-items: center;
  }
`;

const ImageSection = styled.div`
  width: 8rem;
  height: 8rem;

  & img {
    object-fit: cover;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    max-width: 100%;
    min-width: 100%;
    overflow: inherit;

    ${theme.breakpoints.down("sm")} {
      width: 100%;
      height: 100%;
    }
  }
`;

const Image = styled.img``;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 4rem;
  color: black;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: var(--my-color5);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  text-align: ${({ reverse }) => (reverse ? "left" : "right")};

  ${theme.breakpoints.down("sm")} {
    font-size: 1.2rem;
    text-align: center;
  }
`;

const Subtitle = styled.h3`
  font-size: 1.2rem;
  color: var(--my-color2);
  margin: 0rem;
  margin-bottom: 1rem;
  text-align: ${({ reverse }) => (reverse ? "left" : "right")};

  ${theme.breakpoints.down("sm")} {
    font-size: 1.1rem;
    text-align: center;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  margin: 1rem 0;
`;

const BulletList = styled.ul`
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.5rem;
  }
`;

const GallerySection = styled.div`
  background-color: var(--my-color3);
  color: white;
  text-align: left;

  h2 {
    text-transform: uppercase;
    padding: 2rem 1rem 1rem;
    max-width: 800px;
    margin: auto;
    color: white;
  }
`;

const CarouselItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden;
  height: auto;
  aspect-ratio: 1.3;
  border-radius: 10px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  background-color: white;
  padding: 0.5rem;

  img {
    width: ${({ resizeGalleryOn }) =>
      resizeGalleryOn === "width" ? "100%" : "auto"};
    height: ${({ resizeGalleryOn }) =>
      resizeGalleryOn === "height" ? "100%" : "auto"};
    object-fit: contain;
    overflow: hidden;
  }
`;

const CTA = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
  padding: 2rem;

  a {
    background-color: var(--my-color5);
    color: white;
    text-decoration: none;
    padding: 0.75rem 2rem;
    font-weight: bold;
    border-radius: 8px;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: var(--my-color4);
    }
  }
`;

// Modal styling
const ImageModalWrapper = styled.div`
  display: ${({ show }) => (show ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 2rem;
  color: white;
  cursor: pointer;
`;

// --- Composant principal ---
const OffreTemplate = ({
  title,
  intro,
  bannerImage,
  url,
  sections,
  galleryImages,
  resizeGalleryOn = "width",
}) => {
  const [modalImage, setModalImage] = useState(null);

  const location = useLocation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useState(() => {
    scrollToTop();
  }, [location]);

  return (
    <>
      <SEO canonical={url} />
      <Banner image={bannerImage}>
        <BannerTitle dangerouslySetInnerHTML={{ __html: title }}></BannerTitle>
        <BannerSubTitle
          dangerouslySetInnerHTML={{ __html: intro }}
        ></BannerSubTitle>
      </Banner>

      <SectionContainer>
        <Content>
          {sections.map((section, idx) => (
            <AnimateOnScroll key={idx} type="fade">
              <SectionBlock>
                <HeaderSection reverse={idx % 2 !== 0}>
                  <HeaserTextSection reverse={idx % 2 !== 0}>
                    <SectionTitle reverse={idx % 2 !== 0}>
                      {section.title}
                    </SectionTitle>
                    <Subtitle reverse={idx % 2 !== 0}>
                      {section.subtitle}
                    </Subtitle>
                  </HeaserTextSection>
                  <ImageSection>
                    <img src={section.imageSrc} alt={section.title} />
                  </ImageSection>
                </HeaderSection>
                <Paragraph>{section.paragraph}</Paragraph>
                {section.bullets?.length > 0 && (
                  <BulletList>
                    {section.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </BulletList>
                )}
              </SectionBlock>
            </AnimateOnScroll>
          ))}
        </Content>
      </SectionContainer>

      {galleryImages?.length > 0 && (
        <GallerySection>
          <h2>Exemples de réalisations</h2>
          <div style={{ margin: "0rem 1rem" }}>
            <HorizontalCarousel minHeight="280px">
              {galleryImages.map((src, i) => (
                <CarouselItem
                  key={i}
                  resizeGalleryOn={resizeGalleryOn ? resizeGalleryOn : "width"}
                >
                  <img
                    src={src}
                    alt={`Exemple ${i + 1}`}
                    onClick={() => setModalImage(src)}
                    draggable={false}
                  />
                </CarouselItem>
              ))}
            </HorizontalCarousel>
          </div>
        </GallerySection>
      )}

      <CTA>
        <RDVButton label={"Contactez-nous"}></RDVButton>
      </CTA>

      <ImageModalWrapper
        show={!!modalImage}
        onClick={() => setModalImage(null)}
      >
        <CloseButton onClick={() => setModalImage(null)}>&times;</CloseButton>
        {modalImage && <ModalImage src={modalImage} alt="Zoom" />}
      </ImageModalWrapper>
    </>
  );
};

export default OffreTemplate;
